




































import { select, scaleLinear, Selection, ScaleLinear } from 'd3'
import { Guid } from 'guid-typescript'
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'

import FieldTitle from '../Pi/components/FieldTitle.vue'
import { RandomColor } from './logic/helper'

type RatioChartData = {
  series: Array<{name: string, data: number, color?: string}>
}

@Component({
  'components': {
    FieldTitle
  }
})
export default class Ratio extends Vue {
  @Prop() data!: RatioChartData

  @Prop() titleKey!: string

  colors: Record<string, string> = {}

  margin: { x: number, y: number } = {
    x: 5,
    y: 5,
  }

  guid = Guid.create().toString()

  container: Selection<SVGElement, RatioChartData['series'], HTMLElement, any> | undefined

  body: Selection<SVGGElement, RatioChartData['series'], HTMLElement, any> | undefined

  containerWidth = 0

  containerHeight = 0

  scale: ScaleLinear<number, number> | null = null

  percent = scaleLinear()
      .domain([ 0, this.getTotalValue() ])
      .range([ 0, 100 ])

  getTotalValue(): any {
    let totalValue = 0
    for (const value of this.data.series) {
      totalValue += value.data
    }
    return totalValue
  }

  getContainer(): void {
    this.container = select<SVGElement, RatioChartData['series']>(`#container-${ this.guid }`)
        .style('border-radius', '8px')

    const node = this.container.node()

    if (node) {
      this.containerWidth = node.clientWidth
      this.containerHeight = node.clientHeight
    }

    this.body = this.container.append('g')
        .attr('class', 'body')
  }

  getScale(): void {
    this.scale = scaleLinear()
        .domain([ 0, this.getTotalValue() ])
        .range([ 0, this.containerWidth + 2 ])

    this.percent = scaleLinear()
        .domain([ 0, this.getTotalValue() ])
        .range([ 0, 100 ])
  }

  getXPosition(index: number): number {
    if (this.scale) {
      let position = 0

      while (index > 0) {
        position += this.scale(this.data.series[index - 1].data)

        index--
      }

      return position
    }

    return 0
  }

  drawItems(): void {
    this.getScale()

    if (this.body) {
      const volumes = this.body.append('g')
        .attr('class', 'volumes')

      const body = volumes.selectAll('.volume')
        .data(this.data.series)
        .enter()
        .append('g')
        .attr('class', 'volume-body')

      body.append('rect')
        .attr('width', (d) => this.scale && this.scale(d.data))
        .attr('height', this.containerHeight)
        .attr('fill', (d, i: number) => `url(#${ this.drawDecor(d) })`)
        .attr('x', (d, i: number) => this.getXPosition(i))

      volumes.selectAll('.volume-body')
        .append('text')
        .attr('text-anchor', 'middle')
        .style('stroke', 'white')
        .style('fill', 'white')
        .text((d: any) => `${ this.getPercent(d.data) } %`)
        .style('font-size', '20px')
        .attr('x', (d: any, i: number) => {
          if (this.scale) {
            return this.getXPosition(i) + this.scale(d.data) / 2
          }

          return this.getXPosition(i)
        })
        .attr('y', (this.containerHeight + 10) / 2)
    }
  }

  getPercent(num: number): string {
    return this.percent(num).toFixed(2)
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  drawDecor(series: RatioChartData['series'][0]): string {
    if (this.container) {
      for (const node of this.container.selectAll('pattern').nodes()) {
        if (node instanceof HTMLElement && node.attributes?.getNamedItem('id')?.value === series.name.replaceAll(' ', '')) {
          return series.name.replaceAll(' ', '')
        }
      }

      const createColor = this.container.select('defs')
        .append('pattern')
        .attr('id', series.name.replaceAll(' ', ''))
        .attr('width', 15)
        .attr('height', 40)
        .attr('patternUnits', 'userSpaceOnUse')
        .attr('patternTransform', 'rotate(-30)')

      createColor.append('polygon')
        .attr('points', '0 0,10 0,10 40, 0 40,')
        .attr('id', () => {
          if (series.color) {
            return `hex-${ series.color }`
          }

          const color = RandomColor()
          this.colors[series.name] = color

          return `hex-${ color }`
        })
        .attr('fill', () => {
          if (series.color) {
            return `rgba(${ series.color }, 0.9)`
          }

          return this.colors[series.name].replace(', 1)', ', 0.9)')
        })

      createColor.append('rect')
        .attr('width', 5)
        .attr('height', 40)
        .attr('x', 10)
        .attr('y', 0)
        .attr('fill', () => {
          if (series.color) {
            return `rgba(${ series.color }, 1)`
          }

          return this.colors[series.name]
        })

      createColor.append('use')
        .attr('xlink:href', () => {
          if (series.color) {
            return `#hex-${ series.color }`
          }

          return `#hex-${ this.colors[series.name] }`
        })
        .attr('x', 20)

      return series.name.replaceAll(' ', '')
    }

    return '-'
  }

  updateDraw(): void {
    if (this.container) {
      this.container.select('.body').remove()
      this.container.select('defs').remove()

      this.draw()
    }
  }

  draw(): void {
    this.getContainer()

    if (this.container) {
      this.container.append('defs')
    }

    this.drawItems()
  }

  mounted(): void {
    this.draw()
    this.$nextTick(() => {
      // eslint-disable-next-line compat/compat
      const drawObserve = new ResizeObserver((entries: any) => {
        this.updateDraw()
      })

      drawObserve.observe(this.$refs[this.guid] as HTMLElement)
    })
  }
}
